
import AppHeader from "@/components/AppHeader.vue";
import Error from "@/components/Error.vue";
import Page from "@/components/Page.vue";
import Log from "@/views/Log/Log.vue";
import useError from "@/composables/useError";
import {
  PageParamsInputType,
  useGetLogsPaginationQuery,
  useGetOrganisationsQuery,
  useGetApplicationsQuery,
  useGetDistinctEventIdsQuery,
  BasicOrganizationType,
  Maybe,
} from "@/graphql/types";
import { useResult } from "@vue/apollo-composable";
import gql from "graphql-tag";
import { defineComponent, ref, onMounted, watch, computed } from "vue";
import Pagination from "@/components/pagination/Pagination.vue";
import { PageState } from "primevue/paginator";
import useLogSidebar from "@/views/Log/useLogSidebar";
import MultiSelect from "primevue/multiselect";
import useUser from "@/composables/useUser";
import { displayUTCTimestamp } from "@/composables/utils";
import useExportLogsToExcel from "@/composables/useExportLogsToExcel";
// Query
gql`
  query GetLogsPagination(
    $pageParams: PageParamsInputType!
    $filter: LogFilterInput
  ) {
    logsPagination(pageParams: $pageParams, filter: $filter) {
      items {
        ...LogParts
      }
      total
    }
  }
`;
gql`
  query GetDistinctEventIds {
    distinctEventIds {
      id
      description
    }
  }
`;

export default defineComponent({
  inheritAttrs: false,
  components: {
    AppHeader,
    Page,
    Error,
    Pagination,
    Log,
    MultiSelect,
  },
  name: "logs",
  setup() {
    const logSidebar = useLogSidebar();
    const { user } = useUser();
    const pageParams = ref<PageParamsInputType>({
      pageSize: 10,
      currentPage: 1,
    });

    // User role checks
    const isGlobalAdmin = computed(() => user.value?.isSuperAdmin === true);
    const isLocalAdmin = computed(
      () => user.value?.isAdmin === true && !user.value?.isSuperAdmin
    );

    const roles = computed(() => user.value?.roles);
    const isApplicationAdmin = computed(
      () => user.value?.isApplicationAdmin === true
    );

    const filters = ref<any>({});
    const today = new Date();
    const fromDate = new Date(today);
    fromDate.setHours(0, 0, 0, 0);

    const toDate = new Date(today);
    toDate.setHours(23, 59, 59, 999);
    filters.value.fromDate = fromDate;
    filters.value.toDate = toDate;

    // Initialize filter arrays
    const selectedApplications = ref<string[]>([]);
    const selectedOrganizations = ref<string[]>([]);
    const selectedEvents = ref<string[]>([]);

    // Initial GraphQL query
    const { result, loading, error, refetch } = useGetLogsPaginationQuery({
      filter: {
        fromDate: fromDate.toISOString(),
        toDate: toDate.toISOString(),
      },
      pageParams: {
        pageSize: 10,
        currentPage: 1,
      },
    });

    const isFiltering = ref(false);

    function onPageChange(event: PageState) {
      pageParams.value.pageSize = event.rows;
      pageParams.value.currentPage = event.page + 1;
      applyFilters();
    }
    async function applyFilters() {
      isFiltering.value = true;
      try {
        await refetch({ pageParams: pageParams.value, filter: buildFilter() });
      } finally {
        isFiltering.value = false;
      }
    }

    function buildFilter() {
      const filterLocal: any = {};
      if (filters.value.level) filterLocal.level = filters.value.level;
      if (filters.value.type) filterLocal.type = filters.value.type;
      if (filters.value.fromDate)
        filterLocal.fromDate = filters.value.fromDate.toISOString();
      if (filters.value.toDate)
        filterLocal.toDate = filters.value.toDate.toISOString();
      if (filters.value.userId) filterLocal.userId = filters.value.userId;

      if (selectedApplications.value.length > 0)
        filterLocal.applicationIds = selectedApplications.value;

      if (selectedEvents.value.length > 0)
        filterLocal.eventIds = selectedEvents.value;

      if (selectedOrganizations.value.length > 0)
        filterLocal.organizationIds = selectedOrganizations.value;
      return filterLocal;
    }
    function resetFilters() {
      for (const key in filters.value) {
        filters.value[key] = "";
      }
      const today = new Date();
      filters.value.fromDate = today;
      filters.value.toDate = today;
      pageParams.value.currentPage = 1;
      selectedApplications.value = [];
      selectedOrganizations.value = [];
      selectedEvents.value = [];
    }

    const { exportAll, isExporting, exportedCount, batchFrom, finishedCount } =
      useExportLogsToExcel();

    function exportLogsToExcel() {
      exportAll(buildFilter());
    }

    // Application data
    const applications = ref<any[]>([]);
    const { result: appResult } = useGetApplicationsQuery();

    // Organization data
    const organizations = ref<any[]>([]);
    const { result: orgResult, refetch: refetchOrganisations } =
      useGetOrganisationsQuery();

    // Events data
    const events = ref<any[]>([]);
    const { result: eventsResult } = useGetDistinctEventIdsQuery();

    // Watch for application data changes
    watch(appResult, () => {
      const appData = useResult(appResult).value || [];
      setupApplications(appData);
    });

    function setupApplications(appData: any) {
      // Filter applications based on user role
      if (isGlobalAdmin.value) {
        // Global admin sees all applications
        applications.value = [...appData];
      } else if (isLocalAdmin.value && user.value?.organizationId) {
        // Local admin only sees applications assigned to their organization
        // This would require knowing which applications are assigned to which organization
        // For simplicity, assuming all apps are available
        applications.value = [...appData];
      } else if (isApplicationAdmin.value) {
        // Application admin only sees applications they have admin rights for
        applications.value = appData.filter((app: any) => {
          // Check if user has the specific app admin role for this application
          // Role pattern: "SG-IDP-ApplicationAdmin-{applicationName}"
          if (!roles.value || !app.name) return false;

          // Transform application name to match the role format
          // For example, if app name is "User Admin", we'd look for role "SG-IDP-ApplicationAdmin-User-Admin"
          const appNameFormatted = app.name.replace(/\s+/g, "-");
          const expectedRole = `SG-IDP-ApplicationAdmin-${appNameFormatted}`;

          // Check if any user role matches this pattern
          return (roles.value ?? []).some(
            (role) =>
              role === expectedRole ||
              (typeof role === "string" &&
                role.toLowerCase() === expectedRole.toLowerCase())
          );
        });
      } else {
        // Default case - show all
        applications.value = [...appData];
      }
    }

    // Watch for organization data changes
    watch(orgResult, () => {
      const orgData = useResult(orgResult).value || {};
      const items = orgData.items ?? [];
      setupOrganizations(items);
    });
    function setupOrganizations(items: Maybe<BasicOrganizationType>[]) {
      if (isLocalAdmin.value && user.value?.organizationId) {
        organizations.value = items.filter(
          (org: any) => org?.organizationId === user.value?.organizationId
        );
        selectedOrganizations.value = [user.value.organizationId];
      } else {
        // Global admin & app admin (hoặc role khác)
        organizations.value = [...items];
      }
    }
    // Watch for event data changes
    watch(eventsResult, () => {
      events.value = Array.from(useResult(eventsResult).value ?? []);
    });

    onMounted(() => {
      // Initial API call - the watchers will handle populating the data
      const orgData = useResult(orgResult).value || {};
      if (orgData.items) {
        setupOrganizations(orgData.items);
      }

      const appData = useResult(appResult).value;
      if (appData) {
        setupApplications(appData);
      }

      events.value = Array.from(useResult(eventsResult).value ?? []);

      refetch({ pageParams: pageParams.value });
    });

    return {
      logs: useResult(result),
      applications,
      organizations,
      events,
      error: useError(error),
      loading,
      onPageChange,
      pageParams,
      filters,
      resetFilters,
      isFiltering,
      exportLogsToExcel,
      isExporting,
      exportedCount,
      batchFrom,
      finishedCount,
      logSidebar,
      applyFilters,

      // Applications
      selectedApplications,

      // Organizations
      selectedOrganizations,

      // Events
      selectedEvents,

      // User role status
      isGlobalAdmin,
      isLocalAdmin,
      isApplicationAdmin,
      displayUTCTimestamp,
    };
  },
});
